import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, CheckCircle } from 'lucide-react';
import { portalPost } from '../../lib/portalApi';

const SPECIALS = /[!@#$%^&*()_+\-=[\]{};':",./<>?\\|`~]/;

function validate(pw) {
  if (pw.length < 12) return 'Password must be at least 12 characters.';
  if (!/[a-z]/.test(pw)) return 'Must include a lowercase letter.';
  if (!/[A-Z]/.test(pw)) return 'Must include an uppercase letter.';
  if (!/\d/.test(pw)) return 'Must include a number.';
  if (!SPECIALS.test(pw)) return 'Must include a special character (e.g. !@#$%).';
  return null;
}

export default function PortalResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error('Invalid reset link.');
      navigate('/portal/login', { replace: true });
    }
  }, [token, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    const err = validate(newPw);
    if (err) return toast.error(err);
    if (newPw !== confirmPw) return toast.error('Passwords do not match.');
    setLoading(true);
    try {
      await portalPost('/portal/reset-password', { token, new_password: newPw });
      setDone(true);
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || 'Reset failed. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-10 bg-gradient-to-b from-[#0b2f2b] via-[#14504E] to-[#1F7A6E] text-white">
        <img src="/assets/elr-logo-white.png" alt="ELR Industries" className="h-9 w-auto self-start object-contain" />
        <div>
          <div className="text-xs font-display font-bold uppercase tracking-[0.2em] text-white/60 mb-2">Customer Portal</div>
          <h1 className="font-display text-3xl font-extrabold leading-tight mb-3">Secure access,<br />your way.</h1>
          <p className="text-sm text-white/70 max-w-sm">Your new password protects your delivery history, orders and account details.</p>
        </div>
        <div className="text-xs text-white/40">ELR Industries Pvt Ltd</div>
      </div>

      {/* Right form panel */}
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm portal-form-animate">
          <img src="/assets/elr-logo-teal.png" alt="ELR Industries" className="block h-8 w-auto object-contain mb-6 lg:hidden" />

          {!done ? (
            <form onSubmit={submit} className="space-y-4">
              <h2 className="font-display text-2xl font-extrabold text-slate-900">Set a new password</h2>
              <p className="text-sm text-slate-500">Choose a strong password — min 12 characters, upper &amp; lower, number, special character.</p>
              <div>
                <label className="label">New Password</label>
                <input
                  type="password"
                  value={newPw}
                  onChange={(e) => setNewPw(e.target.value)}
                  className="input"
                  placeholder="Min 12 chars"
                  autoFocus
                  required
                  data-testid="portal-reset-new-pw"
                />
              </div>
              <div>
                <label className="label">Confirm Password</label>
                <input
                  type="password"
                  value={confirmPw}
                  onChange={(e) => setConfirmPw(e.target.value)}
                  className="input"
                  placeholder="Repeat password"
                  required
                  data-testid="portal-reset-confirm-pw"
                />
              </div>
              <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="portal-reset-submit">
                {loading ? <Loader2 size={14} className="animate-spin" /> : 'Reset Password'}
              </button>
            </form>
          ) : (
            <div className="space-y-4 text-center" data-testid="portal-reset-success">
              <div className="w-14 h-14 bg-[#1F7A6E]/10 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="text-[#1F7A6E]" size={28} />
              </div>
              <h2 className="font-display text-2xl font-extrabold text-slate-900">Password updated</h2>
              <p className="text-sm text-slate-500">Your password has been reset. You can now sign in with your new password.</p>
              <button
                type="button"
                onClick={() => navigate('/portal/login', { replace: true })}
                className="btn-primary w-full justify-center"
                data-testid="portal-reset-go-login"
              >
                Sign In
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
